import { Component, EventEmitter, Output, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { SliderModule, SliderChangeEvent } from 'primeng/slider';
import { InputNumberModule, InputNumberInputEvent } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectButtonModule } from 'primeng/selectbutton';
import { CardModule } from 'primeng/card';
import { InputSwitchModule } from 'primeng/inputswitch';
import { Subject, takeUntil } from 'rxjs';

import { ContributeWizardService } from '../../services/contribute-wizard.service';

@Component({
  selector: 'app-step-adjust-contribution',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslocoModule,
    ButtonModule,
    SliderModule,
    InputNumberModule,
    InputTextModule,
    SelectButtonModule,
    CardModule,
    InputSwitchModule,
  ],
  templateUrl: './step-adjust-contribution.component.html',
})
export class StepAdjustContributionComponent implements OnInit, OnDestroy {
  @Output() back = new EventEmitter<void>();
  @Output() next = new EventEmitter<void>();

  protected wizardService = inject(ContributeWizardService);
  private transloco = inject(TranslocoService);
  private destroy$ = new Subject<void>();

  isSingleTraveler = false;
  percentage = 100;
  vatNumberInput = '';
  advancedMode = false;

  // Options array (updated on language change)
  coverageOptions: { label: string; value: boolean }[] = [];

  ngOnInit(): void {
    this.isSingleTraveler = this.wizardService.isSingleTraveler();
    this.percentage = this.wizardService.globalPercentage();
    this.vatNumberInput = this.wizardService.vatNumberInput();
    // Auto-enable advanced mode if percentage > 200
    this.advancedMode = this.percentage > 200;
    this.updateOptions();
    // Update options when language changes
    this.transloco.langChanges$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateOptions();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateOptions(): void {
    this.coverageOptions = [
      { label: this.transloco.translate('contribute.coverage.fullTrip'), value: false },
      { label: this.transloco.translate('contribute.coverage.singleTraveler'), value: true },
    ];
  }

  onCoverageChange(value: boolean): void {
    this.wizardService.setCoverageMode(value);
  }

  onSliderChange(event: SliderChangeEvent): void {
    const value = event.value;
    if (typeof value === 'number') {
      this.percentage = value;
      this.wizardService.setPercentage(value);
    }
  }

  onInputChange(event: InputNumberInputEvent): void {
    const value = event.value;
    if (typeof value === 'number') {
      this.percentage = value;
      this.wizardService.setPercentage(value);
    }
  }

  onVatNumberChange(value: string): void {
    this.vatNumberInput = value;
    this.wizardService.setVatNumber(value);
  }

  onBack(): void {
    this.back.emit();
  }

  onNext(): void {
    if (this.wizardService.canProceedStep2()) {
      this.next.emit();
    }
  }
}
